import { useEffect, useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";

import type {
  ColumnFiltersState,
  SortingState,
  RowSelectionState,
  RowData,
} from "@tanstack/react-table";

import { useQuery, keepPreviousData } from "@tanstack/react-query";

import {
  CircularProgress,
  Link,
  Pagination,
  Typography,
  useTheme,
  Select,
  MenuItem,
  Box,
} from "@mui/material";

import type { StackProps } from "@mui/material/Stack";

import {
  fromPairs,
  isEmpty,
  isEqual,
  sortBy,
  startCase,
  sum,
  uniq,
} from "lodash";
import cn from "classnames";

import { useSearchParams } from "react-router-dom";

import { columnDefinitions } from "./columnDefinitions";
import GlobalFilter from "../MonitorsTable/GlobalFilter";
import ColumnFilterSort from "../MonitorsTable/ColumnFilterSort";
import SimpleSort from "../MonitorsTable/ColumnFilterSort/SimpleSort";
import ValuePickerFilter from "../MonitorsTable/ColumnFilterSort/ValuePickerFilter";
import DateThresholdFilter, {
  DateThresholdComparator,
  DateThresholdFilterValue,
} from "../MonitorsTable/ColumnFilterSort/DateThresholdFilter";

import {
  type SelectionAction,
  type TableOrderBy,
  type TableFilterBy,
  type TableRow,
  TableStatus,
} from "../MonitorsTable/types";

import styles from "../MonitorsTable/Table.module.css";
import OverflowTooltip from "../MonitorsTable/OverflowTooltip";

import { ExportRow } from "../MonitorsTable/types";
import GlobalTableStateProvider, {
  useGlobalTableState,
} from "../MonitorsTable/GlobalTableState";

import { useActiveMonitorsDrawer } from "../ActiveMonitorsDrawer";

// This feels hacky as fuck, but is the officially sanctioned way
// of getting the types to work. Woe to you who is trying to
// have multiple table instances with conflicting column defs ¯\_(ツ)_/¯
declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    filterVariant?: "valuePicker" | "dateThreshold";
    filterStickyValues?: string[];
    sortVariant?: "simple";
    resizable?: boolean;
    relativeSize?: number;
    private?: boolean;
  }
}

type TableColumn =
  | "alerts"
  | "assigned_to"
  | "domain"
  | "external_ref_id"
  | "id"
  | "last_alerted"
  | "_unresolved_alerts"
  | "_resolved_alerts";

export const DEFAULT_PAGE_SIZE = 15;
export const DEFAULT_DESCRIPTION = "issues";

export interface Props extends StackProps {
  getMonitors: (config: {
    limit: number;
    offset: number;
    orderBy: TableOrderBy[];
    filterBy: TableFilterBy[];
    fuzzySearch: string;
  }) => Promise<{
    items: TableRow[];
    count: number;
    limit: number;
    offset: number;
  }>;

  getIssuesByMonitorDomains: (
    domains: string[]
  ) => Promise<{ issues: ExportRow[] }>;

  getUniqueValuesForDomain?: () => Promise<string[]>;
  getUniqueValuesForExternalRefId?: () => Promise<string[]>;
  getUniqueValuesForAlerts?: () => Promise<string[]>;
  getUniqueValuesForAssignees?: () => Promise<string[]>;

  onAssignMonitorIssuesToUser?: ({
    monitorIds,
    userId,
  }: {
    monitorIds: UUID[];
    userId: UUID | null | undefined;
    userEmail: string | null | undefined;
  }) => Promise<void>;
  onOptimisticallyUpdateMonitorsAfterAssignment?: ({
    monitorIds,
    userId,
    userEmail,
    items,
  }: {
    monitorIds: UUID[];
    userId: UUID | null | undefined;
    userEmail: string | null | undefined;
    items: TableRow[];
  }) => TableRow[];
  onAssignMonitorIssuesToUserSuccess?: ({
    monitorIds,
    userId,
  }: {
    monitorIds: UUID[];
    userId: UUID | null | undefined;
  }) => Promise<void>;
  onAssignMonitorIssuesToUserError?: ({
    monitorIds,
    userId,
  }: {
    monitorIds: UUID[];
    userId: UUID | null | undefined;
  }) => Promise<void>;

  onOpenMonitorDetail?: (monitorId: UUID, issueId?: UUID) => void;

  prefetch?: () => void;
  description?: string;
  initialFilters?: TableFilterBy[];
  initialSorting?: TableOrderBy[];
  selectable?: boolean;
  selectionActions?: SelectionAction[];
  pageSize?: number;
}

function AlertsTable({
  getMonitors,
  getIssuesByMonitorDomains,

  getUniqueValuesForDomain,
  getUniqueValuesForExternalRefId,
  getUniqueValuesForAlerts,
  getUniqueValuesForAssignees,

  onAssignMonitorIssuesToUser,
  onOptimisticallyUpdateMonitorsAfterAssignment,
  onAssignMonitorIssuesToUserSuccess,
  onAssignMonitorIssuesToUserError,

  onOpenMonitorDetail,

  description = DEFAULT_DESCRIPTION,
  initialFilters = [],
  initialSorting = [],
  selectable = true,
  pageSize = DEFAULT_PAGE_SIZE,
  sx = {},
  ...etc
}: Props) {
  const theme = useTheme();
  const [params, setParams] = useSearchParams();

  const { createMonitor } = useActiveMonitorsDrawer();

  const [globalTableState] = useGlobalTableState();

  const columns: TableColumn[] = useMemo(
    () => [
      "domain",
      "last_alerted",
      "alerts",

      // // private columns used to compute other columns
      "_unresolved_alerts",
      "_resolved_alerts",
    ],
    []
  );

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize });

  const [sorting, setSorting] = useState<SortingState>(
    initialSorting.map((orderBy) => {
      const desc = orderBy.startsWith("-");
      const id = orderBy.replace(/^-/, "");
      return { id, desc };
    })
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    initialFilters.map((initialFilter) => {
      let value: TableFilterBy["value"] | DateThresholdFilterValue =
        initialFilter.value;

      if (
        columnDefinitions[initialFilter.column as TableColumn]?.otherwise.meta
          ?.filterVariant === "dateThreshold"
      ) {
        value = {
          cutoffDate: initialFilter.value as Date,
          comparator: initialFilter.operator as DateThresholdComparator,
        };
      }

      return {
        id: initialFilter.column,
        value,
      };
    })
  );
  const [globalFilter, setGlobalFilter] = useState(params.get("search") || "");

  useEffect(() => {
    setParams((p) => {
      if (!globalFilter) {
        p.delete("search");
      } else {
        p.set("search", globalFilter);
      }

      return p;
    });
  }, [globalFilter, setParams]);

  const listQuery = useQuery({
    queryKey: [
      description,
      "monitors",
      "list",
      pagination,
      sorting,
      columnFilters,
      globalFilter,
    ],
    queryFn: () => {
      let orderBy = sorting.map((col) =>
        col.desc ? `-${col.id}` : col.id
      ) as TableOrderBy[];
      if (orderBy.length === 0) {
        orderBy = ["-last_alerted"];
      }

      return getMonitors({
        orderBy,
        limit: pagination.pageSize,
        offset: pagination.pageIndex * pagination.pageSize,
        filterBy: [
          {
            column: "status",
            operator: "in",
            value: [TableStatus.ALERT, TableStatus.ALERT_AND_PENDING],
          },
          ...columnFilters.map((filter) => {
            const filterVariant =
              columnDefinitions[filter.id as TableColumn].otherwise?.meta
                ?.filterVariant;

            let value = filter.value;
            let operator = "eq";

            if (filterVariant === "valuePicker") {
              operator = "in";
            }

            if (filterVariant === "dateThreshold") {
              operator = (filter.value as any).comparator;
              value = (filter.value as any).cutoffDate;
            }

            return { column: filter.id, value, operator } as TableFilterBy;
          }),
        ],
        fuzzySearch: globalFilter,
      });
    },
    placeholderData: keepPreviousData,
    throwOnError: true,
  });

  const uniqueValuesQuery = useQuery({
    queryKey: [
      description,
      "monitors",
      "uniqueValuesForFilterPickerColumns",
      columns,
    ],
    queryFn: async () => {
      const [domain, external_ref_id, alerts, assignee] = await Promise.all([
        getUniqueValuesForDomain?.(),
        getUniqueValuesForExternalRefId?.(),
        getUniqueValuesForAlerts?.(),
        getUniqueValuesForAssignees?.(),
      ]);
      return {
        domain: domain || [],
        external_ref_id: external_ref_id || [],
        alerts: alerts || [],
        assignee: assignee || [],
      };
    },
  });

  // const assignMonitorIssuesToUsersMutation = useMutation({
  //   mutationFn: async ({
  //     monitorIds,
  //     userId,
  //     userEmail,
  //   }: {
  //     monitorIds: string[];
  //     userId: string | null | undefined;
  //     userEmail: string | null | undefined;
  //   }) => {
  //     if (!onAssignMonitorIssuesToUser) return;
  //     await onAssignMonitorIssuesToUser({ monitorIds, userId, userEmail });
  //   },
  //   mutationKey: ["issues", "assignIssuesToUser"],
  //   onMutate: async (variables) => {
  //     if (!onOptimisticallyUpdateMonitorsAfterAssignment) return;

  //     await queryClient.cancelQueries({
  //       queryKey: [
  //         description,
  //         "monitors",
  //         "list",
  //         pagination,
  //         sorting,
  //         columnFilters,
  //         globalFilter,
  //       ],
  //     });
  //     const previous = queryClient.getQueryData([
  //       description,
  //       "monitors",
  //       "list",
  //       pagination,
  //       sorting,
  //       columnFilters,
  //       globalFilter,
  //     ]);

  //     return { previous };
  //   },
  //   onSuccess: async (data, variables) => {
  //     await queryClient.invalidateQueries({
  //       queryKey: [description, "monitors"],
  //     });

  //     await onAssignMonitorIssuesToUserSuccess?.(variables);
  //   },
  //   onError: (err, variables, context) => {
  //     if (context?.previous) {
  //       queryClient.setQueryData(
  //         [
  //           description,
  //           "monitors",
  //           "list",
  //           pagination,
  //           sorting,
  //           columnFilters,
  //           globalFilter,
  //         ],
  //         context.previous
  //       );
  //     }

  //     showToast();
  //     onAssignMonitorIssuesToUserError?.(variables);
  //   },
  // });

  const isTableLoading = !(listQuery.data && uniqueValuesQuery.data);

  const tableColumns = useMemo(
    () =>
      columns.map((columnName) =>
        isTableLoading
          ? columnDefinitions[columnName].whenLoading
          : columnDefinitions[columnName].otherwise
      ),
    [isTableLoading, columns]
  );

  const dataForLoadingSkeletonTable = useMemo(
    () => Array(pageSize).fill({}),
    [pageSize]
  );

  const table = useReactTable({
    data: isTableLoading ? dataForLoadingSkeletonTable : listQuery.data.items,
    getRowId: (originalRow) => originalRow.id,
    columns: tableColumns,
    enableRowSelection: !!selectable,

    state: {
      rowSelection,
      pagination,
      sorting,
      columnFilters,
      columnVisibility: {
        // make sure private columns are hidden
        ...fromPairs(
          tableColumns.map((columnDef) => {
            if (columnDef.meta?.private) {
              return [columnDef.id, false];
            }
            return [columnDef.id, true];
          })
        ),
      },
    },

    getCoreRowModel: getCoreRowModel(),

    manualPagination: true,
    onPaginationChange: setPagination,

    manualSorting: true,
    onSortingChange: (updaterOrValue) => {
      setSorting(updaterOrValue);
      setPagination((pagination) => ({ ...pagination, pageIndex: 0 }));
    },

    manualFiltering: true,
    onColumnFiltersChange: (updaterOrValue) => {
      setColumnFilters(updaterOrValue);
      setPagination((pagination) => ({ ...pagination, pageIndex: 0 }));
    },

    onRowSelectionChange: setRowSelection,

    columnResizeDirection: "ltr",
    columnResizeMode: "onChange",
  });

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <GlobalFilter
          value={globalFilter}
          onChange={(v) => {
            setGlobalFilter(v);
          }}
          onClear={() => {
            setGlobalFilter("");
          }}
          description={description}
          disabled={globalTableState.disabled}
        />
      </Box>

      {/* TABLE BODY */}

      <div className={cn(styles.tableWrap)}>
        <table className={cn(styles.table)}>
          <thead className={cn(styles.left)}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, i) => {
                  const isResizable = !!header.column.columnDef.meta?.resizable;

                  const resizableColumns = table
                    .getAllColumns()
                    .filter((c) => c.columnDef.meta?.resizable);
                  const totalResizableWidthWeights = sum(
                    resizableColumns.map(
                      (c) => c.columnDef.meta?.relativeSize || 1
                    )
                  );

                  const unweightedWidth = header.getSize(); // assume this is just W / #resizableColumns
                  const totalWidth = unweightedWidth * resizableColumns.length;
                  const weightedColumnWidth =
                    totalWidth *
                    ((header.column.columnDef.meta?.relativeSize || 1) /
                      totalResizableWidthWeights);

                  return (
                    <th
                      key={header.id}
                      className={cn(styles.headercol, {
                        [styles.left]: !isResizable,
                        [styles.small]: !isResizable,
                      })}
                      style={{
                        width: isResizable ? weightedColumnWidth : undefined,
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5em",
                            color:
                              !listQuery.isLoading &&
                              (!!header.column.getIsFiltered() ||
                                !!header.column.getIsSorted())
                                ? theme.palette.primary.main
                                : undefined,
                          }}
                        >
                          <div
                            className={styles.headerlabel}
                            title={
                              startCase(
                                (typeof header.column.columnDef.header ===
                                "string"
                                  ? header.column.columnDef.header
                                  : header.id) || ""
                              ) || undefined
                            }
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </div>
                          {(header.column.columnDef.meta?.sortVariant != null ||
                            header.column.columnDef.meta?.filterStickyValues !=
                              null) && (
                            <ColumnFilterSort
                              key={header.column.id}
                              disabled={
                                !!listQuery.isLoading ||
                                globalTableState.disabled
                              }
                              active={
                                !!header.column.getIsFiltered() ||
                                !!header.column.getIsSorted()
                              }
                              onReset={() => {
                                header.column.setFilterValue(undefined);
                                header.column.clearSorting();
                              }}
                              sx={{ marginRight: "0.5em" }}
                            >
                              {header.column.columnDef.meta?.sortVariant ===
                                "simple" && (
                                <SimpleSort
                                  currentSort={
                                    header.column.getIsSorted() || null
                                  }
                                  onApply={(sort) => {
                                    if (!header.column.getCanSort()) return;

                                    if (!sort) {
                                      header.column.clearSorting();
                                      return;
                                    }

                                    if (sort === "desc") {
                                      header.column.toggleSorting(true, true);
                                      return;
                                    }

                                    if (sort === "asc") {
                                      header.column.toggleSorting(false, true);
                                      return;
                                    }
                                  }}
                                />
                              )}

                              {header.column.columnDef.meta?.filterVariant ===
                                "valuePicker" && (
                                <ValuePickerFilter
                                  allValues={
                                    uniqueValuesQuery.data &&
                                    header.column.id in uniqueValuesQuery.data
                                      ? uniqueValuesQuery.data[
                                          header.column
                                            .id as keyof typeof uniqueValuesQuery.data
                                        ]
                                      : undefined
                                  }
                                  stickyValues={
                                    header.column.columnDef.meta
                                      ?.filterStickyValues
                                  }
                                  selectedValues={
                                    header.column.getIsFiltered()
                                      ? (header.column.getFilterValue() as string[])
                                      : uniqueValuesQuery.data &&
                                        header.column.id in
                                          uniqueValuesQuery.data
                                      ? uniqueValuesQuery.data[
                                          header.column
                                            .id as keyof typeof uniqueValuesQuery.data
                                        ]
                                      : undefined
                                  }
                                  onApply={(
                                    selectedValues: string[] | null | undefined
                                  ) => {
                                    const allValues = sortBy(
                                      uniq(
                                        uniqueValuesQuery.data &&
                                          header.column.id in
                                            uniqueValuesQuery.data
                                          ? uniqueValuesQuery.data[
                                              header.column
                                                .id as keyof typeof uniqueValuesQuery.data
                                            ]
                                          : []
                                      )
                                    );

                                    const selected = sortBy(
                                      uniq(selectedValues || [])
                                    );

                                    if (isEqual(allValues, selected)) {
                                      header.column.setFilterValue(undefined);
                                    } else {
                                      // we can't use header.column.setFilterValue directly
                                      // because some of the defaults/truthiness assumptions
                                      // lead to wonky ux
                                      setColumnFilters((filters) => {
                                        const updatedFilters: ColumnFiltersState =
                                          [
                                            {
                                              id: header.column.id,
                                              value: selected,
                                            },
                                          ];

                                        for (const filter of filters) {
                                          if (filter.id !== header.column.id) {
                                            updatedFilters.push(filter);
                                          }
                                        }

                                        return updatedFilters;
                                      });
                                    }
                                  }}
                                />
                              )}
                              {header.column.columnDef.meta?.filterVariant ===
                                "dateThreshold" && (
                                <DateThresholdFilter
                                  cutoffDate={
                                    header.column.getIsFiltered()
                                      ? (header.column.getFilterValue() as any)
                                          ?.cutoffDate
                                      : undefined
                                  }
                                  comparator={
                                    header.column.getIsFiltered()
                                      ? (header.column.getFilterValue() as any)
                                          ?.comparator
                                      : undefined
                                  }
                                  onApply={(cutoffDate, comparator) => {
                                    if (!cutoffDate) {
                                      setColumnFilters((oldFilters) =>
                                        oldFilters.filter(
                                          (f) => f.id !== header.column.id
                                        )
                                      );
                                    } else {
                                      setColumnFilters((oldFilters) => {
                                        const newFilters = oldFilters.filter(
                                          (f) => f.id !== header.column.id
                                        );
                                        newFilters.push({
                                          id: header.column.id,
                                          value: { comparator, cutoffDate },
                                        });

                                        return newFilters;
                                      });
                                    }
                                  }}
                                />
                              )}
                            </ColumnFilterSort>
                          )}
                          {!!header.column.columnDef.meta?.resizable &&
                            i < headerGroup.headers.length - 1 && (
                              <div
                                onDoubleClick={() => header.column.resetSize()}
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                                className={cn(styles.resizer, {
                                  [styles.isResizing]:
                                    header.column.getIsResizing(),
                                })}
                              />
                            )}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className={styles.left}>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={() => {
                  if (globalTableState.disabled) return;
                  if (isTableLoading) return;
                  onOpenMonitorDetail?.(row.id);
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <OverflowTooltip
                    key={cell.id}
                    tooltipEnabled={!isTableLoading}
                  >
                    <td>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  </OverflowTooltip>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {!table.getRowModel().rows.length && (
          <Typography className={styles.emptyMessage} variant="body2">
            No alerts to show.
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                createMonitor({}, true);
              }}
            >
              Enroll a merchant
            </Link>
          </Typography>
        )}
      </div>

      {/* PAGINATION */}
      <div className={styles.pagination}>
        <div className={styles.rowcounts}>
          {listQuery.data != null && (
            <>
              {table.getRowCount() > 0 && (
                <>
                  {(
                    pagination.pageIndex * pagination.pageSize +
                    1
                  ).toLocaleString()}{" "}
                  -{" "}
                  {Math.min(
                    (pagination.pageIndex + 1) * pagination.pageSize,
                    listQuery.data.count
                  ).toLocaleString()}{" "}
                  of {listQuery.data.count.toLocaleString()}
                </>
              )}
              {!isEmpty(rowSelection) &&
                ` (${Object.keys(rowSelection).length} selected)`}
            </>
          )}
          {((listQuery.isFetching && listQuery.isPlaceholderData) ||
            globalTableState.working) && <CircularProgress size={12} />}
        </div>
        <Pagination
          page={pagination.pageIndex + 1}
          count={
            listQuery.data
              ? Math.ceil(listQuery.data.count / listQuery.data.limit)
              : undefined
          }
          onChange={(_, page) => {
            const pageIndex = Math.max(page - 1, 0);
            table.setPageIndex(pageIndex);
          }}
          size="small"
          showFirstButton
          showLastButton
          disabled={globalTableState.disabled}
        />
        <div className={styles.pagesize}>
          Rows per page:{" "}
          <Select
            labelId="demo-select-small-label"
            id="demo-select-small"
            value={pagination.pageSize}
            onChange={(e) => {
              const pageSize = e.target.value;
              if (typeof pageSize !== "number") return;
              setPagination((v) => ({ ...v, pageSize }));
            }}
            size="small"
          >
            <MenuItem value={15}>15</MenuItem>
            <MenuItem value={30}>30</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </div>
      </div>
    </>
  );
}

export default function AlertsTableWithGlobalState(props: Props) {
  return (
    <GlobalTableStateProvider onOpenMonitorDetail={props.onOpenMonitorDetail}>
      <AlertsTable {...props} />
    </GlobalTableStateProvider>
  );
}
